/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DateInputComponent, PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { downloadWorkbook, save, XlsxSheet } from '../admin/xlsx-export';
import { FormDefinition, isAddress } from './forms.model';
import {
  cellText,
  cellValue,
  FormSubmission,
  isoDay,
  matches,
  ReportColumn,
  reportColumns,
  reportFileName,
  ReportWords,
  submittedAt,
  tableSheet,
  toCsv
} from './forms.report';
import { FormsService } from './forms.service';

/** Rows shown at a time; more come on asking. */
const PAGE = 50;

/**
 * A form's submissions, one row each, with what every field said as the PDF
 * printed it. Administrators only: the answers are people's own. They can be
 * narrowed by words and dates, and saved as CSV or as an Excel workbook (each
 * table field on a sheet of its own).
 */
@Component({
  selector: 'app-form-submissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    DateInputComponent,
    PageHeaderComponent,
    PageStateComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './form-submissions.component.html',
  styleUrls: ['./form-submissions.component.scss']
})
export class FormSubmissionsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly forms = inject(FormsService);
  private readonly translate = inject(TranslateService);

  readonly form = signal<FormDefinition | null>(null);
  readonly all = signal<FormSubmission[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly query = signal('');
  readonly from = signal('');
  readonly to = signal('');
  readonly limit = signal(PAGE);

  readonly columns = computed<ReportColumn[]>(() => {
    const form = this.form();
    return form ? reportColumns(form, this.all()) : [];
  });
  readonly filtered = computed(() => this.all().filter((s) => matches(s, this.query(), this.from(), this.to(), this.columns(), this.words)));
  readonly visible = computed(() => this.filtered().slice(0, this.limit()));
  readonly narrowed = computed(() => !!(this.query().trim() || this.from() || this.to()));

  get words(): ReportWords {
    return {
      yes: this.translate.instant('DMS.FORMS.REPORT_YES'),
      signed: this.translate.instant('DMS.FORMS.REPORT_SIGNED'),
      rows: (count) => this.translate.instant('DMS.FORMS.REPORT_ROWS', { count })
    };
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.loading.set(true);
    this.failed.set('');
    forkJoin({ form: this.forms.get(id), submissions: this.forms.submissions(id) }).subscribe({
      next: ({ form, submissions }) => {
        this.form.set(form);
        this.all.set(submissions ?? []);
        this.limit.set(PAGE);
        this.loading.set(false);
      },
      error: (error) => {
        this.failed.set(error?.status === 403 ? this.translate.instant('DMS.FORMS.REPORT_ADMINS_ONLY') : (error?.message ?? String(error)));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.query.set('');
    this.from.set('');
    this.to.set('');
  }

  more(): void {
    this.limit.update((n) => n + PAGE);
  }

  cell(submission: FormSubmission, column: ReportColumn): string {
    return cellText(column, submission.values?.[column.key], this.words);
  }

  when(submission: FormSubmission): string {
    return submittedAt(submission.at);
  }

  /** The submissions asked for, as a CSV file Excel opens with Thai intact (it needs the byte-order mark). */
  exportCsv(): void {
    const { headers, rows } = this.sheet();
    save(new Blob(['﻿' + toCsv(headers, rows)], { type: 'text/csv;charset=utf-8' }), `${this.fileName()}.csv`);
  }

  /** The submissions asked for as a workbook: one sheet of answers, and one more for each table field's rows. */
  exportExcel(): void {
    const { headers, rows } = this.sheet();
    const sheets: XlsxSheet[] = [{ name: this.translate.instant('DMS.FORMS.REPORT_SHEET'), headers, rows }];
    for (const column of this.columns().filter((c) => c.field?.type === 'table')) {
      const table = tableSheet(column, this.filtered());
      sheets.push({
        name: column.label,
        headers: [this.translate.instant('DMS.FORMS.COL_REFERENCE'), this.translate.instant('DMS.FORMS.COL_ROW'), ...table.headers],
        rows: table.rows
      });
    }
    downloadWorkbook(this.fileName(), sheets);
  }

  /** The answers sheet: who sent what and when, a link to each PDF, then every field (an address also by its province and postcode, to sort and count by). */
  private sheet(): { headers: string[]; rows: (string | number)[][] } {
    const t = (key: string) => this.translate.instant(`DMS.FORMS.${key}`);
    const columns = this.columns();
    const base = `${location.origin}${location.pathname}#`;
    const headers = [
      t('COL_REFERENCE'),
      t('COL_SUBMITTED'),
      t('COL_SENDER'),
      t('COL_SENDER_ID'),
      t('COL_FORM_VERSION'),
      t('COL_SIGNED'),
      ...columns.flatMap((c) =>
        c.field?.type === 'address' ? [c.label, `${c.label} (${t('ADDRESS_PROVINCE')})`, `${c.label} (${t('ADDRESS_POSTCODE')})`] : [c.label]
      ),
      t('COL_DOCUMENT'),
      t('COL_FLOW')
    ];
    const words = this.words;
    const rows = this.filtered().map((s) => [
      s.reference,
      submittedAt(s.at),
      s.byName,
      s.by,
      s.formVersion,
      s.signed ? words.yes : '',
      ...columns.flatMap((c) => {
        const value = s.values?.[c.key];
        const cell = cellValue(c, value, words);
        if (c.field?.type !== 'address') {
          return [cell];
        }
        return isAddress(value) ? [cell, value.province ?? '', value.postcode ?? ''] : [cell, '', ''];
      }),
      s.nodeId ? `${base}/document/${s.nodeId}` : '',
      s.processId ? `${base}/workflows/instances/${s.processId}` : ''
    ]);
    return { headers, rows };
  }

  private fileName(): string {
    return reportFileName(
      this.form()?.title ?? '',
      isoDay(new Date().toISOString()).replace(/^(\d{4})/, (y) => String(Number(y) + 543))
    );
  }
}
